import type { Page } from "./route";

export type Icon =
  | "home"
  | "pin"
  | "drive"
  | "dispatch"
  | "monitor"
  | "review"
  | "process"
  | "sell"
  | "pay"
  | "manage"
  | "analyze"
  | "settings"
  | "admin";

/** One node of the menu tree. A node with a page is a place; a node with children is a group. */
export interface NavNode {
  id: string;
  label: string;
  icon?: Icon;
  page?: Page;
  section?: string;
  view?: string;
  // The detail page under this list, for example one account under Accounts.
  entity?: Page;
  keywords?: string[];
  // The old names of the page: a search for one puts the page first.
  formerly?: string[];
  // A group or a place that only a header button opens; the side menu leaves it out.
  headerOnly?: boolean;
  // A page with tabs: the children are the tabs, and the side menu shows one row.
  tabs?: boolean;
  children?: NavNode[];
}

export type NavPage = NavNode & { page: Page };

// The one list of places (U-7, doc/platform/navigation.md). A node shows only when /api/me/can lists its view.
export const TREE: NavNode[] = [
  {
    id: "overview",
    label: "Home",
    icon: "home",
    page: "overview",
    view: "review",
    keywords: ["overview", "start", "dashboard", "today", "map", "gallons", "drivers", "all is well"],
  },
  {
    id: "drive",
    label: "Drive",
    icon: "drive",
    keywords: ["driver", "phone", "route day"],
    children: [
      {
        id: "today",
        label: "Route",
        page: "today",
        view: "route_run",
        keywords: ["today", "my route", "stops", "driver", "pickups", "gallons", "skip", "run route"],
        formerly: ["today"],
      },
      {
        id: "visit",
        label: "Record a visit",
        page: "visit",
        view: "visit",
        keywords: ["pickup", "gallons", "level", "took nothing", "locked", "driver", "phone"],
      },
      {
        id: "sync",
        label: "Sync",
        page: "sync",
        view: "visit",
        headerOnly: true,
        keywords: ["offline", "saved on phone", "waiting", "send now", "not saved", "outbox", "signal"],
      },
    ],
  },
  {
    id: "dispatch",
    label: "Dispatch",
    icon: "dispatch",
    keywords: ["operations", "dispatcher", "plan", "trucks"],
    children: [
      {
        id: "routes",
        label: "Routes",
        page: "routes",
        view: "route_run",
        keywords: ["drivers", "trucks", "stop order", "day plan", "make routes", "today"],
      },
      {
        id: "planner",
        label: "Route planner",
        page: "route-planner",
        view: "fleet",
        keywords: ["route planner", "minimum visit", "inspection", "visit period", "56 days"],
      },
      {
        id: "cycle",
        label: "Cycle board",
        page: "cycle",
        view: "route",
        keywords: ["plan", "rotation week", "stops", "dispatch", "truck-days", "capacity", "csv"],
      },
      {
        id: "requests",
        label: "Requests",
        page: "requests",
        view: "pickup_request",
        keywords: ["pickup requests", "service", "container", "lock", "call in", "need by", "late", "scheduled", "collected", "cancel"],
      },
    ],
  },
  {
    id: "monitor",
    label: "Monitor",
    icon: "monitor",
    keywords: ["operations", "watch", "alerts"],
    children: [
      {
        id: "monitor-containers",
        label: "Containers",
        tabs: true,
        keywords: ["drums", "totes"],
        children: [
          {
            id: "tanks",
            label: "Levels",
            page: "tanks",
            entity: "tank",
            keywords: ["tanks", "container level", "levels", "sensors", "devices", "home"],
          },
          {
            id: "fill",
            label: "Fill forecast",
            page: "fill",
            view: "fill_view",
            keywords: ["fill view", "fill", "full", "estimate", "map", "bands", "overflow", "next two weeks"],
          },
        ],
      },
      {
        id: "issues",
        label: "Issues",
        page: "issues",
        view: "issue",
        keywords: ["problems", "tasks", "complaints", "quality", "assigned"],
      },
      {
        id: "messages",
        label: "Messages",
        page: "messages",
        view: "customer_message",
        keywords: ["customer messages", "portal", "support", "restaurant", "gate code", "message", "customer portal"],
      },
      {
        id: "work-orders",
        label: "Work orders",
        page: "work-orders",
        view: "container",
        keywords: ["containers", "swap", "resize", "sticker", "pick list", "yard"],
      },
    ],
  },
  {
    id: "reviews",
    label: "Review",
    icon: "review",
    keywords: ["review", "check", "approve", "office"],
    children: [
      {
        id: "review",
        label: "Pickups",
        page: "review",
        view: "review",
        keywords: ["flags", "overflow", "small container", "rate", "scrap", "weekly", "decisions"],
      },
      {
        id: "visits",
        label: "Visits",
        page: "visits",
        view: "visit",
        keywords: ["driver tickets", "enter visits", "pickups", "grid", "correct", "office", "day"],
      },
      {
        id: "parity",
        label: "Parity",
        page: "parity",
        view: "parity",
        keywords: ["sheet", "shadow run", "compare", "agree", "differences", "explain", "cutover"],
      },
    ],
  },
  {
    id: "process",
    label: "Process",
    icon: "process",
    keywords: ["facility", "processor"],
    children: [
      {
        id: "loads",
        label: "Loads",
        page: "loads",
        view: "load",
        keywords: ["trucks", "processor", "scrap", "reconcile", "approve", "facility"],
      },
    ],
  },
  {
    id: "sell",
    label: "Sell",
    icon: "sell",
    keywords: ["sales"],
    children: [
      {
        id: "leads",
        label: "Leads",
        page: "leads",
        entity: "lead",
        view: "prospect",
        keywords: ["prospects", "pipeline", "inbox", "qualified", "proposal", "won", "lost", "new customers"],
      },
      {
        id: "follow-ups",
        label: "Follow-ups",
        page: "follow-ups",
        view: "prospect",
        keywords: ["follow up", "due", "call back", "queue", "missing data", "reminders"],
      },
      {
        id: "deals",
        label: "Deals",
        page: "deals",
        entity: "deal",
        view: "activation",
        keywords: ["deal sheet", "sign up", "new account", "activation", "approve", "contract", "agreement"],
      },
      {
        id: "renewals",
        label: "Renewals",
        page: "renewals",
        view: "contract",
        keywords: ["contracts", "expiry", "ends", "re-sign", "check-in", "renew", "ended"],
      },
    ],
  },
  {
    id: "pay",
    label: "Pay",
    icon: "pay",
    keywords: ["accounting", "payouts", "money"],
    children: [
      {
        id: "rebates",
        label: "Rebates",
        page: "rebates",
        entity: "rebate",
        view: "payout",
        keywords: ["checks", "payouts", "quarterly", "quickbooks", "payee", "approve", "export", "paid"],
      },
      {
        id: "rebate-minimum",
        label: "Rebate checks",
        page: "rebate-checks",
        view: "payout",
        keywords: ["minimum", "minimum check", "rebates", "payouts", "checks"],
      },
    ],
  },
  {
    id: "manage",
    label: "Manage",
    icon: "manage",
    keywords: ["records", "master data"],
    children: [
      {
        id: "accounts",
        label: "Accounts",
        page: "accounts",
        entity: "account",
        view: "account",
        keywords: ["customers", "restaurants", "pickups", "service", "plans"],
      },
      {
        id: "manage-containers",
        label: "Containers",
        tabs: true,
        keywords: ["drums", "totes"],
        children: [
          {
            id: "containers",
            label: "Sizes",
            page: "containers",
            view: "account",
            keywords: ["sizes", "gallons", "recommended size", "container sizes"],
          },
          {
            id: "inventory",
            label: "Units",
            page: "inventory",
            entity: "unit",
            view: "inventory",
            keywords: ["inventory", "serial numbers", "units", "yard", "loaded", "deployed", "reconditioning"],
          },
        ],
      },
      {
        id: "fleet",
        label: "Fleet",
        page: "fleet",
        view: "fleet",
        keywords: ["depots", "trucks", "hubs", "capacity", "truck sizes", "truck-days", "shifts", "route planner"],
      },
    ],
  },
  {
    id: "analyze",
    label: "Analyze",
    icon: "analyze",
    keywords: ["reports", "numbers", "history"],
    children: [
      {
        id: "scorecard",
        label: "Scorecard",
        page: "scorecard",
        view: "scorecard",
        keywords: ["metrics", "kpi", "calls at fault", "overflows", "accuracy", "weekly", "numbers"],
      },
      {
        id: "pickups",
        label: "Pickups history",
        page: "pickups",
        view: "review",
        keywords: ["pickups of a day", "gallons", "all pickups", "sheet", "raw", "scrap"],
      },
      {
        id: "size-savings",
        label: "Size savings",
        page: "size-savings",
        view: "account",
        keywords: ["treemap", "heat map", "minutes saved", "payback", "best size", "larger container"],
      },
    ],
  },
  {
    id: "admin",
    label: "Admin",
    icon: "admin",
    keywords: ["admin", "admin area", "settings", "organization settings"],
    children: [
      {
        id: "users",
        label: "Users",
        page: "users",
        entity: "user",
        view: "member",
        keywords: ["members", "invite", "roles", "people", "staff", "deactivate", "access", "role matrix", "permission matrix"],
      },
      {
        id: "roles",
        label: "Roles",
        page: "roles",
        entity: "role",
        view: "member",
        keywords: ["custom roles", "permissions", "access", "built-in roles"],
      },
      {
        id: "keys",
        label: "API keys",
        page: "keys",
        view: "api_key",
        keywords: ["api", "keys", "integrations", "greasr", "token", "revoke"],
      },
      {
        id: "import",
        label: "Import",
        page: "import",
        view: "import",
        keywords: ["service sheet", "sales sheet", "import", "source", "pause", "run now", "dry run", "clean-up", "issues", "spreadsheet", "pins to check", "sales final import", "lock", "competitors", "previous collector", "payees"],
      },
      {
        id: "cutover",
        label: "Cutover",
        page: "cutover",
        view: "cutover",
        keywords: ["cutover", "switch", "go live", "sheet to app", "readiness", "ready", "shadow", "watch", "areas on the app", "switch back"],
      },
      {
        id: "activity",
        label: "Activity",
        page: "activity",
        view: "activity",
        keywords: ["audit", "log", "history", "who changed", "changes", "chain check", "area switch", "downloads"],
      },
      {
        id: "exports",
        label: "Exports",
        page: "exports",
        view: "export",
        keywords: ["csv", "download", "export", "backup", "sheet shape", "pickups", "accounts", "parity", "way back"],
      },
      {
        id: "calendar",
        label: "Cycle calendar",
        page: "admin",
        section: "calendar",
        view: "calendar",
        keywords: ["week start", "cycle start", "rotation weeks", "holiday rule", "early pull", "margin"],
      },
      {
        id: "holidays",
        label: "Holidays",
        page: "admin",
        section: "holidays",
        view: "holiday",
        keywords: ["holidays", "days off", "closed", "next visit", "calendar"],
      },
      {
        id: "sales-areas",
        label: "Sales areas",
        page: "admin",
        section: "sales-areas",
        view: "area",
        keywords: ["salespeople", "areas", "territory", "lead inbox", "owner", "assign leads"],
      },
      {
        id: "sales-terms",
        label: "Sales terms",
        page: "admin",
        section: "sales-terms",
        view: "lookup",
        keywords: ["account numbers", "ranges", "states", "rebate limits", "approval", "deals"],
      },
      {
        id: "forecast",
        label: "Forecast limits",
        page: "admin",
        section: "forecast",
        view: "forecast",
        keywords: ["flags", "near full", "scrap", "rate band", "small container"],
      },
      {
        id: "container-settings",
        label: "Containers",
        page: "admin",
        section: "containers",
        view: "forecast",
        keywords: ["container sizes", "standard sizes", "margin", "recommended size", "gallons"],
      },
      {
        id: "sensor-rules",
        label: "Sensor rules",
        page: "admin",
        section: "sensor-rules",
        view: "forecast",
        keywords: ["stuck", "theft", "spike", "silence", "offline", "anomaly", "alerts"],
      },
      {
        id: "pre-trip",
        label: "Pre-trip check",
        page: "admin",
        section: "pre-trip",
        view: "lookup",
        keywords: ["inspection", "checklist", "odometer", "trucks", "drivers", "vehicle"],
      },
      {
        id: "visit-labels",
        label: "Visit labels",
        page: "admin",
        section: "visit-labels",
        view: "visit_label",
        keywords: ["labels", "tags", "visit notes", "chips", "drivers", "record a visit"],
      },
      {
        id: "issue-reminders",
        label: "Issue reminders",
        page: "admin",
        section: "issue-reminders",
        view: "lookup",
        keywords: ["issue reminder", "old issues", "days", "mail", "assignee", "notifications"],
      },
      {
        id: "place-check",
        label: "Away check",
        page: "admin",
        section: "place-check",
        view: "lookup",
        keywords: ["away", "distance", "stop pin", "gps", "place check", "driver", "review"],
      },
      {
        id: "telegram-alerts",
        label: "Telegram alerts",
        page: "admin",
        section: "telegram-alerts",
        view: "alert",
        keywords: ["telegram", "alerts", "theft", "silence", "sensor issues", "channel", "chat", "notifications"],
      },
      {
        id: "message-mail",
        label: "Message mail",
        page: "admin",
        section: "message-mail",
        view: "lookup",
        keywords: ["customer messages", "portal", "email", "mail", "salesperson", "notifications"],
      },
      {
        id: "portal-contact",
        label: "Customer portal contact",
        page: "admin",
        section: "portal-contact",
        view: "lookup",
        keywords: ["customer portal", "restaurant", "phone", "email", "who to contact", "contact"],
      },
    ],
  },
  {
    id: "your-settings",
    label: "Your settings",
    icon: "settings",
    headerOnly: true,
    children: [
      {
        id: "your-account",
        label: "Your account",
        page: "settings",
        section: "your-account",
        keywords: ["sign out", "email", "profile", "organizations", "switch"],
      },
      { id: "theme", label: "Theme", page: "settings", section: "theme", keywords: ["dark", "light", "colors"] },
      {
        id: "notifications",
        label: "Notifications",
        page: "settings",
        section: "notifications",
        keywords: ["mail", "email", "follow-up mail", "morning mail", "alerts", "issue reminder mail"],
      },
      {
        id: "menu",
        label: "Menu",
        page: "settings",
        section: "menu",
        keywords: ["side menu", "expand all", "open all", "groups", "collapse"],
      },
    ],
  },
];

export const isPage = (node: NavNode): node is NavPage => node.page !== undefined;

const HOME_ID = "overview";
const ADMIN_ID = "admin";
const rank = (node: NavNode) => (node.id === HOME_ID ? 0 : node.headerOnly ? 3 : node.id === ADMIN_ID ? 2 : 1);

/** The page with tabs that holds this node, if any. */
export function tabsOf(id: string | undefined, tree: readonly NavNode[] = TREE): NavNode | undefined {
  const parent = ancestors(id, tree).at(-1);
  return parent?.tabs ? parent : undefined;
}

/** The label of a place out of its menu: "Containers · Levels" for a tab, "Admin · Containers" for a repeated name. */
export function fullLabel(page: NavNode, tree: readonly NavNode[] = TREE): string {
  const parent = tabsOf(page.id, tree);
  if (parent) return `${parent.label} · ${page.label}`;
  const same = walk(tree).filter(({ node }) => node.id !== page.id && node.label === page.label && (isPage(node) || node.tabs));
  const top = ancestors(page.id, tree)[0];
  return same.length && top && top.id !== page.id ? `${top.label} · ${page.label}` : page.label;
}

/** The page that the pin of a menu row acts on: the open tab of a row with tabs, else its first tab. */
export function pinTarget(row: NavNode, current: string | undefined): NavPage | undefined {
  if (isPage(row)) return row;
  const tabs = (row.children ?? []).filter(isPage);
  return tabs.find((tab) => tab.id === current) ?? tabs[0];
}

/** The side menu order (U-7): Home first, Admin last, the other groups and all pages from A to Z. */
export function menuOrder(tree: readonly NavNode[]): NavNode[] {
  return [...tree]
    .map((node) => (node.children && !node.tabs ? { ...node, children: menuOrder(node.children) } : node))
    .sort((a, b) => rank(a) - rank(b) || a.label.localeCompare(b.label));
}

/** Each node with its groups from the root, in menu order. */
export function walk(tree: readonly NavNode[] = TREE, above: NavNode[] = []): { node: NavNode; above: NavNode[] }[] {
  return tree.flatMap((node) => [{ node, above }, ...walk(node.children ?? [], [...above, node])]);
}

/** The tree with only the nodes of the views of the role, and no empty group. */
export function visible(views: readonly string[] | undefined, tree: readonly NavNode[] = TREE): NavNode[] {
  return tree.flatMap((node): NavNode[] => {
    if (node.view && !views?.includes(node.view)) return [];
    if (isPage(node)) return [node];
    const children = visible(views, node.children ?? []);
    return children.length ? [{ ...node, children }] : [];
  });
}

export function pages(tree: readonly NavNode[] = TREE): NavPage[] {
  return walk(tree)
    .map(({ node }) => node)
    .filter(isPage);
}

export function pathOf(page: NavPage): string {
  const path = `/${page.page}`;
  return page.section ? `${path}#${page.section}` : path;
}

/** The groups above a node, from the root. */
export function ancestors(id: string | undefined, tree: readonly NavNode[] = TREE): NavNode[] {
  return walk(tree).find(({ node }) => node.id === id)?.above ?? [];
}

/** The breadcrumb of a node: its groups, then the node. */
export function trail(id: string | undefined, tree: readonly NavNode[] = TREE): NavNode[] {
  const found = walk(tree).find(({ node }) => node.id === id);
  return found ? [...found.above, found.node] : [];
}

/** The group that the menu opens first for each role; the first role in ROLE_HOME order wins. */
export const ROLE_GROUP: Readonly<Record<string, string>> = {
  administrator: "monitor",
  operations_manager: "monitor",
  office: "reviews",
  dispatcher: "dispatch",
  sales_manager: "sell",
  salesperson: "sell",
  driver: "drive",
  accounting: "pay",
  viewer: "analyze",
  facility_operator: "process",
};

/** The main group of the user that the menu shows, else none. */
export function mainGroup(roles: readonly string[], tree: readonly NavNode[]): string | undefined {
  return ROLE_HOME.map(([role]) => role)
    .filter((role) => roles.includes(role))
    .map((role) => ROLE_GROUP[role])
    .find((id) => tree.some((node) => node.id === id));
}

/** The groups to open after a change: the main group only when it changes, so a closed main group stays closed. */
export function opensOnChange(
  before: { main?: string; path: readonly string[] },
  after: { main?: string; path: readonly string[] },
): string[] {
  const main = after.main && after.main !== before.main ? [after.main] : [];
  const path = after.path.join(" ") !== before.path.join(" ") ? after.path : [];
  return [...main, ...path];
}

export type MenuHeld = { open: string[]; main?: string; path: string[]; known?: string[] };
type MenuSpot = { main?: string; path: readonly string[] };

/** The main group and the path that a change is measured from: the held ones, so a reload opens no closed group. */
export function menuBaseline(held: MenuHeld | null, now: MenuSpot): MenuSpot {
  return held ? { main: held.main, path: held.path } : now;
}

/** The groups to open after a change of the props; nothing while the roles are not known (U-34). */
export function menuStep(seen: MenuSpot, after: MenuSpot, known: boolean): { open: string[]; seen: MenuSpot } {
  return known ? { open: opensOnChange(seen, after), seen: after } : { open: [], seen };
}
export const MENU_KEY = "xflow.menu-open";
export const MENU_ALL_KEY = "xflow.menu-all";

/** The first page of a node with tabs: the side menu shows that node as one link, not as a group. */
export const tabLink = (node: NavNode): NavPage | undefined => (node.tabs ? node.children?.find(isPage) : undefined);

/** The groups that the side menu can open: each node that it shows as a group button, with its subgroups. */
export function groupIds(tree: readonly NavNode[]): string[] {
  return tree.flatMap((node) =>
    node.headerOnly || isPage(node) || tabLink(node) ? [] : [node.id, ...groupIds(node.children ?? [])],
  );
}

export function allOpen(open: ReadonlySet<string>, ids: readonly string[]): boolean {
  return ids.every((id) => open.has(id));
}

/** The held set and the opensOnChange groups, else the fresh set; each group of `all` (U-34) that this device has not seen opens. */
export function openAtMount(
  held: MenuHeld | null,
  fresh: readonly string[],
  now: { main?: string; path: readonly string[] },
  all?: readonly string[],
): Set<string> {
  const known = new Set(held?.known ?? []);
  const unseen = (all ?? []).filter((id) => !known.has(id));
  if (!held) return new Set([...fresh, ...unseen]);
  return new Set([...held.open, ...opensOnChange(held, now), ...unseen]);
}

const menuKey = (uid: string) => `${MENU_KEY}.${uid}`;

const asHeld = (raw: string | null): MenuHeld | null => {
  const held = JSON.parse(raw ?? "null") as MenuHeld | null;
  return held && Array.isArray(held.open) && Array.isArray(held.path) ? held : null;
};

/** The open groups of the last menu of this person on this device; the old per-tab value is read one time. */
export function readMenuHeld(uid: string): MenuHeld | null {
  try {
    const held = asHeld(localStorage.getItem(menuKey(uid)));
    if (held) return held;
  } catch {
    // No storage: try the old per-tab value.
  }
  try {
    return asHeld(sessionStorage.getItem(MENU_KEY));
  } catch {
    return null;
  }
}

export function writeMenuHeld(uid: string, held: MenuHeld): void {
  try {
    localStorage.setItem(menuKey(uid), JSON.stringify(held));
  } catch {
    // No storage: the drawer starts with the fresh set.
  }
}

/** The cached "open all menu groups" setting of the person on this device, else undefined. */
export function readMenuAll(uid: string): boolean | undefined {
  try {
    const value = localStorage.getItem(`${MENU_ALL_KEY}.${uid}`);
    return value === null ? undefined : value === "1";
  } catch {
    return undefined;
  }
}

export function writeMenuAll(uid: string, on: boolean): void {
  try {
    localStorage.setItem(`${MENU_ALL_KEY}.${uid}`, on ? "1" : "0");
  } catch {
    // No storage: the menu waits for the server value at each load.
  }
}

/** The roles whose menu shows only these groups (U-7); a user with one more role sees each group. */
export const MENU_ONLY: Readonly<Record<string, readonly string[]>> = { driver: ["drive"], facility_operator: ["process"] };

/** The groups of the side menu, the search and the wrench; the pages stay open by link. */
export function menuFor(roles: readonly string[] | null | undefined, tree: readonly NavNode[]): NavNode[] {
  if (!roles?.length || roles.some((role) => !MENU_ONLY[role])) return [...tree];
  const keep = new Set(roles.flatMap((role) => MENU_ONLY[role]));
  return tree.filter((node) => node.headerOnly || keep.has(node.id));
}

/** The path that the Admin wrench opens: the top of the /admin page, else its first list. */
export function adminPath(tree: readonly NavNode[]): string | undefined {
  const group = tree.find((node) => node.id === ADMIN_ID);
  const all = group ? pages([group]) : [];
  const first = all.find((page) => page.section) ?? all[0];
  return first && (first.section ? `/${first.page}` : pathOf(first));
}

/** The sections of the /admin page, in the order that AdminArea shows them. */
export const ADMIN_SECTIONS = [
  "forecast",
  "sensor-rules",
  "containers",
  "calendar",
  "holidays",
  "sales-areas",
  "sales-terms",
  "pre-trip",
  "visit-labels",
  "issue-reminders",
  "place-check",
  "telegram-alerts",
  "message-mail",
  "portal-contact",
] as const;

/** The /admin sections that are now pages of their own, by their old section id. */
export const OWN_PAGES: Readonly<Record<string, Page>> = {
  fleet: "fleet",
  planner: "route-planner",
  "rebate-minimum": "rebate-checks",
};

/** The new path of an old link: /settings#section to /admin#section, and /admin#fleet to /fleet. */
export function movedSection(page: Page, hash: string, tree: readonly NavNode[] = TREE): string | undefined {
  const section = hash.replace(/^#/, "");
  if (!section) return undefined;
  if (OWN_PAGES[section] && (page === "admin" || page === "settings")) return `/${OWN_PAGES[section]}`;
  if (page !== "settings") return undefined;
  return pages(tree).some((p) => p.page === "admin" && p.section === section) ? `/admin#${section}` : undefined;
}

/** The main page of each role (U-8). The first role in this order that the user has wins. */
export const ROLE_HOME: readonly (readonly [role: string, page: string])[] = [
  ["administrator", "overview"],
  ["operations_manager", "overview"],
  ["office", "overview"],
  ["dispatcher", "overview"],
  ["sales_manager", "accounts"],
  ["salesperson", "accounts"],
  ["driver", "today"],
  ["accounting", "scorecard"],
  ["viewer", "scorecard"],
  ["facility_operator", "loads"],
];

/** On a phone, these roles start on another page than ROLE_HOME, when they can see it. */
export const PHONE_HOME: Readonly<Record<string, string>> = { driver: "visit" };

/** False when the menu knows the page and the role sees no entry of it; record pages are always true. */
export function pageAllowed(page: Page, views: readonly string[], tree: readonly NavNode[] = TREE): boolean {
  if (!pages(tree).some((known) => known.page === page)) return true;
  return pages(visible(views, tree)).some((known) => known.page === page);
}

/** The page that /o/<slug> opens: the home of the role, else the first page the role can see. */
export function homePage(
  roles: readonly string[],
  views: readonly string[] | undefined,
  tree: readonly NavNode[] = TREE,
  phone = false,
): NavPage | undefined {
  const shown = pages(visible(views, tree)).filter((page) => !page.section);
  const order = ROLE_HOME.flatMap(([role, id]) => (phone && PHONE_HOME[role] ? [[role, PHONE_HOME[role]] as const, [role, id] as const] : [[role, id] as const]));
  const home = order.find(([role, id]) => roles.includes(role) && shown.some((page) => page.id === id));
  return (home && shown.find((page) => page.id === home[1])) ?? shown[0];
}

/** The node that holds this route; a detail page belongs to the list above it. `shown` holds the sections of the user. */
export function currentId(page: Page, hash = "", tree: readonly NavNode[] = TREE, shown: readonly NavNode[] = tree): string | undefined {
  const all = pages(tree);
  if (page === "settings" || page === "admin") {
    const section = hash.replace(/^#/, "");
    const settings = all.filter((p) => p.page === page);
    const seen = pages(shown);
    const firstShown = ADMIN_SECTIONS.find((id) => seen.some((p) => p.page === "admin" && p.section === id));
    const top = settings.find((p) => p.section === firstShown) ?? settings.find((p) => ancestors(p.id, tree)[0]?.id === ADMIN_ID);
    return (settings.find((p) => p.section === section) ?? (page === "admin" ? top : undefined) ?? settings[0])?.id;
  }
  const entity = page === "agreement" ? "deal" : page;
  return (all.find((p) => p.page === page && !p.section) ?? all.find((p) => p.entity === entity))?.id;
}

/** Is the route this exact node, and not a detail page under it? */
export function isExact(page: Page, tree: readonly NavNode[] = TREE): boolean {
  return page !== "agreement" && !pages(tree).some((p) => p.entity === page);
}

export const MAX_PINS = 20;

/** The pinned pages that the role can view, in the order of the pins. Hidden pins stay stored. */
export function pinnedGroup(pins: readonly string[], tree: readonly NavNode[]): NavNode | undefined {
  const shown = pages(tree);
  const found = pins
    .map((id) => shown.find((page) => page.id === id))
    .filter((page): page is NavPage => page !== undefined)
    .map((page) => ({ ...page, label: fullLabel(page, tree) }));
  return found.length ? { id: "pinned", label: "Pinned", icon: "pin", children: found } : undefined;
}

/** Add or remove one pin. A new pin goes at the end; the other pins keep their order. */
export function setPin(pins: readonly string[], id: string, on: boolean): string[] {
  if (pins.includes(id) === on) return [...pins];
  return togglePin(pins, id);
}

export function togglePin(pins: readonly string[], id: string): string[] {
  if (pins.includes(id)) return pins.filter((pin) => pin !== id);
  return pins.length >= MAX_PINS ? [...pins] : [...pins, id];
}
